/**
 * Generated by orval 🍺
 * Do not edit manually.
 * ILP Content Delivery API
 * API for delivering educational content including course structures and glossaries
 * OpenAPI spec version: v1
 */
import useSwr from "swr";
import type { Arguments, Key, SWRConfiguration } from "swr";

import useSWRMutation from "swr/mutation";
import type { SWRMutationConfiguration } from "swr/mutation";

import type {
  CourseAttachments,
  CreateEnrollmentRequest,
  Enrollment,
  EnrollmentCourseStructure,
  EnrollmentCreated,
  EnrollmentSummary,
  Error,
  ListEnrollmentsParams,
  Peers,
  Trainers,
  UpdateEnrollmentRequest,
} from "../model";

import { apiFetch } from "../../apiFetch";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

export const getListEnrollmentAttachmentsUrl = (id: string) => {
  return `/api/v1/enrollments/${id}/attachments`;
};

/**
 * Course documents for the dashboard, scoped to the audience of the enrollment owner's role: a participant's enrollment lists the participant files, a trainer's enrollment the trainer files, whoever asks.
 * @summary List the course documents of an enrollment
 */
export const listEnrollmentAttachments = async (
  id: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<CourseAttachments> => {
  return apiFetch<CourseAttachments>(getListEnrollmentAttachmentsUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getListEnrollmentAttachmentsKey = (id: string) =>
  [`/api/v1/enrollments/${id}/attachments`] as const;

export type ListEnrollmentAttachmentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listEnrollmentAttachments>>
>;

/**
 * @summary List the course documents of an enrollment
 */
export const useListEnrollmentAttachments = <TError = Error>(
  id: string,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof listEnrollmentAttachments>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false && id !== null && id !== undefined;
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getListEnrollmentAttachmentsKey(id) : null));
  const swrFn = () => listEnrollmentAttachments(id, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
export const getListEnrollmentsUrl = (params?: ListEnrollmentsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/enrollments?${stringifiedParams}`
    : `/api/v1/enrollments`;
};

/**
 * Retrieves enrollments for the currently authenticated user. When called with a system token, pass userExternalId to list a specific user's enrollments instead.
 * @summary List enrollments
 */
export const listEnrollments = async (
  params?: ListEnrollmentsParams,
  options?: Parameters<typeof apiFetch>[1]
): Promise<EnrollmentSummary[]> => {
  return apiFetch<EnrollmentSummary[]>(getListEnrollmentsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListEnrollmentsKey = (params?: ListEnrollmentsParams) =>
  [`/api/v1/enrollments`, ...(params ? [params] : [])] as const;

export type ListEnrollmentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listEnrollments>>
>;

/**
 * @summary List enrollments
 */
export const useListEnrollments = <TError = unknown>(
  params?: ListEnrollmentsParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof listEnrollments>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getListEnrollmentsKey(params) : null));
  const swrFn = () => listEnrollments(params, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
export const getCreateEnrollmentUrl = () => {
  return `/api/v1/enrollments`;
};

/**
 * Creates a new enrollment or returns the existing one (idempotent by user + schedule)
 * @summary Create an enrollment
 */
export const createEnrollment = async (
  createEnrollmentRequest?: CreateEnrollmentRequest,
  options?: Parameters<typeof apiFetch>[1]
): Promise<EnrollmentCreated> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string]
        )
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<EnrollmentCreated>(getCreateEnrollmentUrl(), {
    ...options,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getHeaders(options?.headers),
    },
    body: JSON.stringify(createEnrollmentRequest),
  });
};

export const getCreateEnrollmentMutationFetcher = (
  options?: SecondParameter<typeof apiFetch>
) => {
  return (_: Key, { arg }: { arg: CreateEnrollmentRequest | undefined }) => {
    return createEnrollment(arg, options);
  };
};
export const getCreateEnrollmentMutationKey = () =>
  [`/api/v1/enrollments`] as const;

export type CreateEnrollmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof createEnrollment>>
>;

/**
 * @summary Create an enrollment
 */
export const useCreateEnrollment = <TError = void | Error>(options?: {
  swr?: SWRMutationConfiguration<
    Awaited<ReturnType<typeof createEnrollment>>,
    TError,
    Key,
    CreateEnrollmentRequest | undefined,
    Awaited<ReturnType<typeof createEnrollment>>
  > & { swrKey?: string };
  request?: SecondParameter<typeof apiFetch>;
}) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const swrKey = swrOptions?.swrKey ?? getCreateEnrollmentMutationKey();
  const swrFn = getCreateEnrollmentMutationFetcher(requestOptions);

  const query = useSWRMutation(swrKey, swrFn, swrOptions);

  return {
    swrKey,
    ...query,
  };
};
export const getGetEnrollmentUrl = (id: string) => {
  return `/api/v1/enrollments/${id}`;
};

/**
 * Retrieves the enrollment details including its learning progress
 * @summary Show enrollment details and progress
 */
export const getEnrollment = async (
  id: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<Enrollment> => {
  return apiFetch<Enrollment>(getGetEnrollmentUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetEnrollmentKey = (id: string) =>
  [`/api/v1/enrollments/${id}`] as const;

export type GetEnrollmentQueryResult = NonNullable<
  Awaited<ReturnType<typeof getEnrollment>>
>;

/**
 * @summary Show enrollment details and progress
 */
export const useGetEnrollment = <TError = Error>(
  id: string,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof getEnrollment>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false && id !== null && id !== undefined;
  const swrKey =
    swrOptions?.swrKey ?? (() => (isEnabled ? getGetEnrollmentKey(id) : null));
  const swrFn = () => getEnrollment(id, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
export const getUpdateEnrollmentUrl = (id: string) => {
  return `/api/v1/enrollments/${id}`;
};

/**
 * Updates mutable progress fields on an enrollment. The caller must own the enrollment (or be an admin or system-token caller).
 * @summary Update enrollment progress
 */
export const updateEnrollment = async (
  id: string,
  updateEnrollmentRequest?: UpdateEnrollmentRequest,
  options?: Parameters<typeof apiFetch>[1]
): Promise<void> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string]
        )
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<void>(getUpdateEnrollmentUrl(id), {
    ...options,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getHeaders(options?.headers),
    },
    body: JSON.stringify(updateEnrollmentRequest),
  });
};

export const getUpdateEnrollmentMutationFetcher = (
  id: string,
  options?: SecondParameter<typeof apiFetch>
) => {
  return (_: Key, { arg }: { arg: UpdateEnrollmentRequest | undefined }) => {
    return updateEnrollment(id, arg, options);
  };
};
export const getUpdateEnrollmentMutationKey = (id: string) =>
  [`/api/v1/enrollments/${id}`] as const;

export type UpdateEnrollmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateEnrollment>>
>;

/**
 * @summary Update enrollment progress
 */
export const useUpdateEnrollment = <TError = unknown>(
  id: string,
  options?: {
    swr?: SWRMutationConfiguration<
      Awaited<ReturnType<typeof updateEnrollment>>,
      TError,
      Key,
      UpdateEnrollmentRequest | undefined,
      Awaited<ReturnType<typeof updateEnrollment>>
    > & { swrKey?: string };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const swrKey = swrOptions?.swrKey ?? getUpdateEnrollmentMutationKey(id);
  const swrFn = getUpdateEnrollmentMutationFetcher(id, requestOptions);

  const query = useSWRMutation(swrKey, swrFn, swrOptions);

  return {
    swrKey,
    ...query,
  };
};
export const getDeleteEnrollmentUrl = (id: string) => {
  return `/api/v1/enrollments/${id}`;
};

/**
 * Removes an enrollment (idempotent — returns 204 even if not found)
 * @summary Delete an enrollment
 */
export const deleteEnrollment = async (
  id: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<void> => {
  return apiFetch<void>(getDeleteEnrollmentUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteEnrollmentMutationFetcher = (
  id: string,
  options?: SecondParameter<typeof apiFetch>
) => {
  return (_: Key, __: { arg: Arguments }) => {
    return deleteEnrollment(id, options);
  };
};
export const getDeleteEnrollmentMutationKey = (id: string) =>
  [`/api/v1/enrollments/${id}`] as const;

export type DeleteEnrollmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteEnrollment>>
>;

/**
 * @summary Delete an enrollment
 */
export const useDeleteEnrollment = <TError = void>(
  id: string,
  options?: {
    swr?: SWRMutationConfiguration<
      Awaited<ReturnType<typeof deleteEnrollment>>,
      TError,
      Key,
      Arguments,
      Awaited<ReturnType<typeof deleteEnrollment>>
    > & { swrKey?: string };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const swrKey = swrOptions?.swrKey ?? getDeleteEnrollmentMutationKey(id);
  const swrFn = getDeleteEnrollmentMutationFetcher(id, requestOptions);

  const query = useSWRMutation(swrKey, swrFn, swrOptions);

  return {
    swrKey,
    ...query,
  };
};
export const getListPeersUrl = (id: string) => {
  return `/api/v1/enrollments/${id}/peers`;
};

/**
 * Retrieves all active participants enrolled in the same schedule
 * @summary List peers in the same schedule
 */
export const listPeers = async (
  id: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<Peers> => {
  return apiFetch<Peers>(getListPeersUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getListPeersKey = (id: string) =>
  [`/api/v1/enrollments/${id}/peers`] as const;

export type ListPeersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPeers>>
>;

/**
 * @summary List peers in the same schedule
 */
export const useListPeers = <TError = unknown>(
  id: string,
  options?: {
    swr?: SWRConfiguration<Awaited<ReturnType<typeof listPeers>>, TError> & {
      swrKey?: Key;
      enabled?: boolean;
    };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false && id !== null && id !== undefined;
  const swrKey =
    swrOptions?.swrKey ?? (() => (isEnabled ? getListPeersKey(id) : null));
  const swrFn = () => listPeers(id, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
export const getListTrainersUrl = (id: string) => {
  return `/api/v1/enrollments/${id}/trainers`;
};

/**
 * Retrieves all active trainers assigned to the course via enrollment
 * @summary List trainers for the enrollment's course
 */
export const listTrainers = async (
  id: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<Trainers> => {
  return apiFetch<Trainers>(getListTrainersUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getListTrainersKey = (id: string) =>
  [`/api/v1/enrollments/${id}/trainers`] as const;

export type ListTrainersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTrainers>>
>;

/**
 * @summary List trainers for the enrollment's course
 */
export const useListTrainers = <TError = Error>(
  id: string,
  options?: {
    swr?: SWRConfiguration<Awaited<ReturnType<typeof listTrainers>>, TError> & {
      swrKey?: Key;
      enabled?: boolean;
    };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false && id !== null && id !== undefined;
  const swrKey =
    swrOptions?.swrKey ?? (() => (isEnabled ? getListTrainersKey(id) : null));
  const swrFn = () => listTrainers(id, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
export const getGetEnrollmentCourseStructureUrl = (id: string) => {
  return `/api/v1/enrollments/${id}/course_structure`;
};

/**
 * Returns the course tree as it was snapshotted into the enrollment's course version. Unlike GET /courses/{external_id}, later changes in the Content Factory do not leak into an active enrollment.
 * @summary Show the snapshotted course structure for an enrollment
 */
export const getEnrollmentCourseStructure = async (
  id: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<EnrollmentCourseStructure> => {
  return apiFetch<EnrollmentCourseStructure>(
    getGetEnrollmentCourseStructureUrl(id),
    {
      ...options,
      method: "GET",
    }
  );
};

export const getGetEnrollmentCourseStructureKey = (id: string) =>
  [`/api/v1/enrollments/${id}/course_structure`] as const;

export type GetEnrollmentCourseStructureQueryResult = NonNullable<
  Awaited<ReturnType<typeof getEnrollmentCourseStructure>>
>;

/**
 * @summary Show the snapshotted course structure for an enrollment
 */
export const useGetEnrollmentCourseStructure = <TError = Error>(
  id: string,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof getEnrollmentCourseStructure>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false && id !== null && id !== undefined;
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getGetEnrollmentCourseStructureKey(id) : null));
  const swrFn = () => getEnrollmentCourseStructure(id, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
